import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export function checkForValidExpirationDate(prefix: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) return null; // Parent not set yet

    // Access sibling controls by prefix (e.g., 'id_expiration' or 'no_nics_valid_permit_expiration')
    const month = control.parent.get(`${prefix}_month`)?.value;
    const day = control.parent.get(`${prefix}_day`)?.value;
    const year = control.parent.get(`${prefix}_year`)?.value;
    // Only validate if all fields are filled
    if (month && day && year) {
      // Basic format validation
      if (
        !/^\d{1,2}$/.test(month) ||
        !/^\d{1,2}$/.test(day) ||
        !/^\d{4}$/.test(year)
      ) {
        return { invalidFormat: true };
      }
      // Validate date
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expirationDate = new Date(
        `${Number(year)}-${Number(month)}-${Number(day)}`
      );
      expirationDate.setHours(0, 0, 0, 0);

      const monthControl = control.parent.get(`${prefix}_month`);
      const dayControl = control.parent.get(`${prefix}_day`);
      const yearControl = control.parent.get(`${prefix}_year`);

      if (isNaN(expirationDate.getTime()) || expirationDate < today) {
        // Set errors on all three controls
        monthControl?.setErrors({ invalidDate: true });
        dayControl?.setErrors({ invalidDate: true });
        yearControl?.setErrors({ invalidDate: true });

        monthControl?.markAsDirty();
        dayControl?.markAsDirty();
        yearControl?.markAsDirty();

        monthControl?.markAsTouched();
        dayControl?.markAsTouched();
        yearControl?.markAsTouched();
        return { invalidDate: true };
      } else {
        // Clear errors if valid
        monthControl?.setErrors(null);
        dayControl?.setErrors(null);
        yearControl?.setErrors(null);
      }
    }
    return null; // Valid or incomplete
  };
}

export function minimumAgeValidator(minAge: number): (control: AbstractControl) => ValidationErrors | null {
  let birthDateString: string;
  return (control: AbstractControl): ValidationErrors | null => {
    const birthDateStr = control.value;
    if (!birthDateStr) return null;

    // Ensure birthDateStr is a string
    if (birthDateStr instanceof Date) {
      // Format Date object to MM/DD/YYYY
      const mm = String(birthDateStr.getMonth() + 1).padStart(2, '0');
      const dd = String(birthDateStr.getDate()).padStart(2, '0');
      const yyyy = birthDateStr.getFullYear();
      birthDateString = `${mm}/${dd}/${yyyy}`;
    } else {
      birthDateString = String(birthDateStr);
    }

    // Parse MM/DD/YYYY
    const [month, day, year] = birthDateString.split('/').map(Number);
    if (!month || !day || !year) return null;

    const birthDate = new Date(year, month - 1, day);
    const today = new Date();

    // Calculate age
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= minAge
      ? null
      : { minimumAge: { required: minAge, actual: age } };
  };
}

export function notFutureDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    const parts = value.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts.map(Number);
      if (month && day && year) {
        date = new Date(year, month - 1, day);
      }
    }
  }
  if (!date) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Only return error if date is after today
  return date > today ? { futureDate: true } : null;
}

export function notPastDateValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string') {
    const parts = value.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts.map(Number);
      if (month && day && year) {
        date = new Date(year, month - 1, day);
      }
    }
  }
  if (!date) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Valid if expiration date is today or in the future
  return date < today ? { pastDate: true } : null;
}